/**
 * Billing Status Card Component
 *
 * Displays current billing status including plan tier, usage limits, and upgrade CTA.
 * - Free tier: Shows usage progress bar (e.g., "7 of 10 pings used today") with upgrade button
 * - Pro tier: Shows "Pro Plan" badge with "Unlimited pings" text
 *
 * @see docs/PRD.md for billing tier requirements
 * @see app/src/workers/api-routes.ts for billing status API
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Box,
  ProgressBar,
  Banner,
  SkeletonBodyText,
  SkeletonDisplayText,
} from '@shopify/polaris';
import type { BillingStatus, ApiResult } from '../types/api';
import { fetchBillingStatus, initiateBillingUpgrade } from '../lib/api-client';

// =============================================================================
// Types
// =============================================================================

export interface BillingStatusCardProps {
  /** Optional pre-loaded billing status (if not provided, component fetches on mount) */
  initialStatus?: BillingStatus | null;
  /** Callback when billing status changes (e.g., after upgrade) */
  onStatusChange?: () => void;
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Calculate progress bar percentage for usage
 */
function calculateProgress(used: number, limit: number | null): number {
  if (limit === null || limit === 0) return 0;
  return Math.min(Math.round((used / limit) * 100), 100);
}

/**
 * Get progress bar tone based on usage percentage
 */
function getProgressTone(percentage: number): 'success' | 'highlight' | 'critical' {
  if (percentage >= 100) return 'critical';
  if (percentage >= 80) return 'highlight';
  return 'success';
}

/**
 * Format activation date for display
 */
function formatActivationDate(isoString: string | null): string | null {
  if (!isoString) return null;
  try {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return null;
  }
}

/**
 * Open URL in parent window (breaks out of Shopify iframe)
 * Required for Shopify billing flow which cannot run inside iframe
 */
function redirectToUrl(url: string): void {
  if (typeof window !== 'undefined') {
    // Use _top to break out of iframe and redirect the entire page
    // This is required for Shopify billing confirmation
    window.open(url, '_top');
  }
}

// =============================================================================
// Skeleton Component
// =============================================================================

/**
 * Loading skeleton for BillingStatusCard
 */
function BillingStatusCardSkeleton() {
  return (
    <Card>
      <BlockStack gap="400">
        <SkeletonBodyText lines={1} />
        <SkeletonDisplayText size="small" />
        <SkeletonBodyText lines={2} />
      </BlockStack>
    </Card>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Card displaying billing status with usage tracking and upgrade option.
 *
 * Features:
 * - Free tier: Shows 'Free Plan' with usage progress bar
 * - Progress bar shows dailyUsed / dailyLimit (e.g., '7 of 10 pings used today')
 * - Shows 'Upgrade to Pro' button with CTA
 * - Pro tier: Shows 'Pro Plan' badge with 'Unlimited pings' text
 * - Optionally shows subscription activation date
 * - Loading skeleton state
 * - Error handling for API failures
 * - Handles redirect to Shopify billing confirmation URL
 *
 * @example
 * ```tsx
 * // Self-loading component
 * <BillingStatusCard onStatusChange={handleRefresh} />
 *
 * // With pre-loaded status
 * <BillingStatusCard
 *   initialStatus={billingStatus}
 *   onStatusChange={handleRefresh}
 * />
 * ```
 */
export function BillingStatusCard({
  initialStatus,
  onStatusChange: _onStatusChange,
}: BillingStatusCardProps) {
  const [status, setStatus] = useState<BillingStatus | null>(initialStatus ?? null);
  const [loading, setLoading] = useState(!initialStatus);
  const [error, setError] = useState<string | null>(null);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);

  // Fetch billing status on mount if not provided
  useEffect(() => {
    if (initialStatus) {
      setStatus(initialStatus);
      setLoading(false);
      return;
    }

    async function loadBillingStatus() {
      setLoading(true);
      setError(null);

      try {
        const result: ApiResult<BillingStatus> = await fetchBillingStatus();

        if (result.success) {
          setStatus(result.data);
        } else {
          setError(result.error.error || 'Failed to load billing status');
        }
      } catch (err) {
        setError('Failed to load billing status');
      } finally {
        setLoading(false);
      }
    }

    loadBillingStatus();
  }, [initialStatus]);

  // Handle upgrade button click
  const handleUpgrade = useCallback(async () => {
    setUpgrading(true);
    setUpgradeError(null);

    try {
      const result: ApiResult<{ confirmationUrl: string }> = await initiateBillingUpgrade();

      if (result.success && result.data.confirmationUrl) {
        // Redirect to Shopify billing confirmation page
        // This breaks out of the iframe and takes the merchant to Shopify's billing UI
        redirectToUrl(result.data.confirmationUrl);
      } else if (!result.success) {
        setUpgradeError(result.error.error || 'Failed to initiate upgrade');
        setUpgrading(false);
      }
    } catch (err) {
      setUpgradeError('Failed to initiate upgrade. Please try again.');
      setUpgrading(false);
    }
    // Note: We don't reset upgrading=false on success because the page will redirect
  }, []);

  // Handle retry after error
  const handleRetry = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result: ApiResult<BillingStatus> = await fetchBillingStatus();

      if (result.success) {
        setStatus(result.data);
      } else {
        setError(result.error.error || 'Failed to load billing status');
      }
    } catch (err) {
      setError('Failed to load billing status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Loading state
  if (loading) {
    return <BillingStatusCardSkeleton />;
  }

  // Error state
  if (error) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm" tone="subdued">
            Billing Status
          </Text>
          <Banner
            tone="critical"
            action={{ content: 'Retry', onAction: handleRetry }}
          >
            {error}
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  // No status (shouldn't happen but handle gracefully)
  if (!status) {
    return (
      <Card>
        <BlockStack gap="300">
          <Text as="h3" variant="headingSm" tone="subdued">
            Billing Status
          </Text>
          <Text as="p" variant="bodyMd" tone="subdued">
            Unable to load billing information.
          </Text>
        </BlockStack>
      </Card>
    );
  }

  // Pro tier display
  if (status.tier === 'pro') {
    const activationDate = formatActivationDate(status.subscriptionActivatedAt);

    return (
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h3" variant="headingSm" tone="subdued">
              Billing Status
            </Text>
            <Badge tone="success">Pro Plan</Badge>
          </InlineStack>

          <BlockStack gap="200">
            <Text as="p" variant="headingMd" fontWeight="semibold">
              Unlimited pings
            </Text>
            <Text as="p" variant="bodySm" tone="subdued">
              You have unlimited access to ping search engines when your store content changes.
            </Text>
          </BlockStack>

          {activationDate && (
            <Text as="p" variant="bodySm" tone="subdued">
              Pro since {activationDate}
            </Text>
          )}
        </BlockStack>
      </Card>
    );
  }

  // Free tier display
  const { dailyUsed, dailyLimit, dailyRemaining } = status;
  const progressPercentage = calculateProgress(dailyUsed, dailyLimit);
  const progressTone = getProgressTone(progressPercentage);
  const limitReached = dailyRemaining !== null && dailyRemaining <= 0;

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingSm" tone="subdued">
            Billing Status
          </Text>
          <Badge>Free Plan</Badge>
        </InlineStack>

        {/* Usage display */}
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="p" variant="bodyMd">
              Today's Usage
            </Text>
            <Text as="p" variant="bodyMd" fontWeight="semibold">
              {dailyUsed} of {dailyLimit} pings used
            </Text>
          </InlineStack>

          <ProgressBar
            progress={progressPercentage}
            tone={progressTone}
            size="small"
          />

          <Text as="p" variant="bodySm" tone="subdued">
            {limitReached
              ? 'Daily limit reached. Resets at midnight Pacific Time.'
              : `${dailyRemaining} pings remaining today`}
          </Text>
        </BlockStack>

        {/* Limit reached warning */}
        {limitReached && (
          <Banner tone="warning">
            You've reached your daily ping limit. Upgrade to Pro for unlimited pings.
          </Banner>
        )}

        {/* Upgrade error */}
        {upgradeError && (
          <Banner tone="critical" onDismiss={() => setUpgradeError(null)}>
            {upgradeError}
          </Banner>
        )}

        {/* Upgrade CTA */}
        <Box>
          <Button
            variant="primary"
            onClick={handleUpgrade}
            loading={upgrading}
          >
            Upgrade to Pro
          </Button>
        </Box>

        <Text as="p" variant="bodySm" tone="subdued">
          $20/month for unlimited pings to all search engines.
        </Text>
      </BlockStack>
    </Card>
  );
}
